import {MS_PER_DAY} from '@common/constants/time';

export function maskSsn(ssn: string | null | undefined): string {
  if (!ssn) return 'N/A';
  const digits = ssn.replace(/\D/g, '');
  if (digits.length < 4) return 'N/A';
  return `***-**-${digits.slice(-4)}`;
}

/** Renders a full SSN as `XXX-XX-XXXX` when 9 digits; otherwise returns the raw digits. */
export function formatSsn(ssn: string | null | undefined): string {
  if (!ssn) return 'N/A';
  const digits = ssn.replace(/\D/g, '');
  if (digits.length === 0) return 'N/A';
  if (digits.length !== 9) return digits;
  return `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5)}`;
}

/**
 * Trimmed string, or `null` when it is absent or blank.
 *
 * Note this differs from `value?.trim() ?? null`, which preserves `''`. Use this
 * where an empty string and a missing value mean the same thing — a blank optional
 * field, say — and the distinction would otherwise leak into storage or a query
 * predicate as a filter on the empty string.
 */
/** Boolean as a human-readable cell value, for CSVs and read-only detail fields. */
export function yesNo(value: boolean): string {
  return value ? 'Yes' : 'No';
}

/**
 * A single uppercase initial from a full middle name, or `''`.
 *
 * Sources that store the whole middle name need narrowing wherever only the initial
 * is displayed or exported.
 */
export function middleInitial(middleName: string | null | undefined): string {
  const trimmed = middleName?.trim();
  return trimmed ? trimmed.charAt(0).toUpperCase() : '';
}

export function trimToNull(value: string | null | undefined): string | null {
  const trimmed = value?.trim();
  return trimmed ? trimmed : null;
}

/**
 * `City, ST 12345` from whichever parts are present.
 *
 * Separators are driven by what's actually there rather than written between fixed
 * slots: address parts are unevenly populated — many groups carry none at all — and
 * the fixed-slot form renders an empty address as a bare `", "`.
 */
export function formatCityStateZip(
  city: string | null | undefined,
  state: string | null | undefined,
  zipCode: string | null | undefined,
): string {
  const cityState = [trimToNull(city), trimToNull(state)].filter(Boolean).join(', ');
  return [cityState, trimToNull(zipCode)].filter(Boolean).join(' ');
}

interface DateParts {
  day: number;
  month: number;
  year: number;
}

// Birth dates are date-only values, so they're read in UTC; "today" is the viewer's local date.
function utcDateParts(date: string | Date): DateParts {
  const d = typeof date === 'string' ? new Date(date) : date;
  return {day: d.getUTCDate(), month: d.getUTCMonth(), year: d.getUTCFullYear()};
}

function localDateParts(date: Date): DateParts {
  return {day: date.getDate(), month: date.getMonth(), year: date.getFullYear()};
}

export function computeAge(birthDateIso: string, asOf: Date = new Date()): number {
  const birth = utcDateParts(birthDateIso);
  const now = localDateParts(asOf);
  let age = now.year - birth.year;
  if (now.month < birth.month || (now.month === birth.month && now.day < birth.day)) {
    age -= 1;
  }
  return age;
}

export function daysUntilAge(birthDateIso: string, age: number, asOf: Date = new Date()): number {
  const birth = utcDateParts(birthDateIso);
  const now = localDateParts(asOf);
  const target = Date.UTC(birth.year + age, birth.month, birth.day);
  const today = Date.UTC(now.year, now.month, now.day);
  return Math.ceil((target - today) / MS_PER_DAY);
}

export function composeVbaMemberId(subscriberId: string, memberSeq: string): string {
  return `${subscriberId}-${memberSeq}`;
}

/** A US phone as its 10 digits, dropping a leading 1 country code; null if it isn't one. */
export function parseUsPhone(phone: string): string | null {
  const digits = phone.replace(/\D/g, '');
  if (digits.length === 10) return digits;
  if (digits.length === 11 && digits.startsWith('1')) return digits.slice(1);
  return null;
}

export function formatPhoneNumber(phone: string): string | null {
  if (!phone) return null;

  const digits = phone.replace(/\D/g, '');

  if (digits.length === 10) {
    return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
  }

  if (digits.length === 11 && digits.startsWith('1')) {
    const tenDigits = digits.slice(1);
    return `${tenDigits.slice(0, 3)}-${tenDigits.slice(3, 6)}-${tenDigits.slice(6)}`;
  }

  return null;
}

/**
 * `formatPhoneNumber` for display: input it can't format is returned as typed, and empty input
 * returns null so callers pick their own placeholder.
 */
export function formatPhoneDisplay(phone: string | null | undefined): string | null {
  if (!phone) return null;
  return formatPhoneNumber(phone) ?? phone;
}

/**
 * Formats a value as USD currency. Accepts numbers, numeric strings, and
 * `Decimal`-like objects (anything with a `.toString()` that yields a numeric
 * string — covers Prisma `Decimal` values straight off the wire). Nullish or
 * non-numeric input renders as the `fallback` (default `$0.00`).
 *
 * `maximumFractionDigits: 0` gives whole-dollar rendering (e.g. deal values);
 * `minimumFractionDigits` follows it down automatically so Intl never throws.
 */
export const formatCurrency = (
  amount: number | string | {toString: () => string} | null | undefined,
  opts?: {fallback?: string; minimumFractionDigits?: number; maximumFractionDigits?: number},
) => {
  const fallback = opts?.fallback ?? '$0.00';
  if (amount == null) return fallback;
  const raw = typeof amount === 'object' ? amount.toString() : amount;
  const value = Number(raw);
  if (!Number.isFinite(value)) return fallback;
  const maximumFractionDigits = opts?.maximumFractionDigits ?? 2;
  const minimumFractionDigits = opts?.minimumFractionDigits ?? Math.min(2, maximumFractionDigits);
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits,
    maximumFractionDigits,
  }).format(value);
};

export const SHORT_MONTH_NAMES = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
] as const;

export const LONG_MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
] as const;

/**
 * VBA / date-only fields (Birth_Date, Date_Enrolled, plan starts/ends, etc.) are stored at
 * midnight without a meaningful timezone. The non-`short` styles format using UTC getters so
 * `1985-09-03T00:00:00Z` always renders as Sep 3, never Sep 2 in a westward locale.
 *
 * `'short'` keeps `Intl.DateTimeFormat` with local-tz semantics for values that genuinely
 * represent a moment in time (e.g. timestamps on internal jobs/audits).
 */
export function formatDate(
  date: string | Date | null | undefined,
  style: 'short' | 'MM/DD/YYYY' | 'MMM D, YYYY' | 'MMM D' = 'short',
  fallback = '',
): string {
  if (!date) return fallback;
  const d = typeof date === 'string' ? new Date(date) : date;
  if (Number.isNaN(d.getTime())) return fallback;
  if (style === 'MM/DD/YYYY') {
    const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
    const dd = String(d.getUTCDate()).padStart(2, '0');
    const yyyy = d.getUTCFullYear();
    return `${mm}/${dd}/${yyyy}`;
  }
  if (style === 'MMM D') {
    return `${SHORT_MONTH_NAMES[d.getUTCMonth()]} ${d.getUTCDate()}`;
  }
  if (style === 'MMM D, YYYY') {
    const mon = SHORT_MONTH_NAMES[d.getUTCMonth()];
    const day = d.getUTCDate();
    const yyyy = d.getUTCFullYear();
    return `${mon} ${day}, ${yyyy}`;
  }
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  }).format(d);
}

/**
 * Formats a value that represents a real moment in time (e.g. audit/job timestamps) including the
 * time component, using local-timezone semantics. Unlike `formatDate`, this does not use UTC getters
 * because the value is an instant, not a date-only field.
 */
export function formatDateTime(date: string | Date | null | undefined, fallback = ''): string {
  if (!date) return fallback;
  const d = typeof date === 'string' ? new Date(date) : date;
  if (Number.isNaN(d.getTime())) return fallback;
  return d.toLocaleString();
}

export function uuid() {
  return Math.random().toString(36).substring(7);
}

export function safeObjectEntries<T extends object>(obj: T): [keyof T, T[keyof T]][] {
  return Object.entries(obj) as [keyof T, T[keyof T]][];
}
export function safeObjectKeys<T extends object>(obj: T): (keyof T)[] {
  return Object.keys(obj) as (keyof T)[];
}
export function safeObjectValues<T extends object>(obj: T): T[keyof T][] {
  return Object.values(obj) as T[keyof T][];
}

export function timeout(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}
export function delay(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}
export function unreachable(x: never): never {
  throw new Error('unreachable');
}
export function groupBy<T, TKey extends string | number>(
  arr: T[],
  callback: (el: T) => TKey,
): Array<{
  key: TKey;
  items: T[];
}> {
  const groups: Record<TKey, T[]> = {} as any;

  arr.forEach((el) => {
    const key = callback(el);
    if (!groups[key]) {
      groups[key] = [];
    }
    groups[key].push(el);
  });

  return Object.entries(groups).map(([key, items]) => ({key, items})) as any;
}
export function groupByMap<T, TKey extends string | number>(
  arr: T[],
  callback: (el: T) => TKey,
): Map<TKey, T[]> {
  const groups: Record<TKey, T[]> = {} as any;

  arr.forEach((el) => {
    const key = callback(el);
    if (!groups[key]) {
      groups[key] = [];
    }
    groups[key].push(el);
  });

  return new Map(safeObjectEntries(groups));
}
export function sum<T>(arr: T[], callback: (el: T) => number): number {
  return arr.reduce((acc, el) => acc + callback(el), 0);
}
export function unique<T, T2>(arr: T[], callback: (el: T) => T2): T[] {
  const seen = new Set<T2>();
  return arr.filter((el) => {
    const key = callback(el);
    if (seen.has(key)) {
      return false;
    }
    seen.add(key);
    return true;
  });
}

export function batchItems<T, T2 = void>(
  items: T[],
  action: (item: T) => Promise<T2>,
  batchSize: number,
): Promise<T2[]> {
  return new Promise<T2[]>((resolve, reject) => {
    const results: T2[] = [];
    let currentIndex = 0;
    // console.log('Starting batch processing of', items.length, 'items with batch size', batchSize);

    const processBatch = async () => {
      if (currentIndex >= items.length) {
        resolve(results);
        return;
      }

      const batch = items.slice(currentIndex, currentIndex + batchSize);
      try {
        // console.log(
        //   `Processing batch: items ${currentIndex + 1} to ${
        //     currentIndex + batch.length
        //   } of ${items.length}`,
        // );
        await Promise.all(
          batch.map(async (item) => {
            results.push(await action(item));
          }),
        );
        currentIndex += batchSize;

        processBatch();
      } catch (error) {
        reject(error);
      }
    };

    processBatch();
  });
}
export function chunk<T>(items: T[], batchSize: number): Array<T[]> {
  const batches: T[][] = [];
  for (let i = 0; i < items.length; i += batchSize) {
    batches.push(items.slice(i, i + batchSize));
  }
  return batches;
}

export function JSONToCSV(json: any[]) {
  const keys = Object.keys(json[0]);
  const csvRows = [
    keys.map((key) => `"${key}"`).join(','), // header row
    ...json.map((row) => keys.map((key) => `"${String(row[key] ?? '')}"`).join(',')), // data rows
  ];
  return csvRows.join('\n');
}

/**
 * Calculate Levenshtein distance between two strings
 */
export function levenshteinDistance(str1: string, str2: string): number {
  const matrix = Array(str2.length + 1)
    .fill(null)
    .map(() => Array(str1.length + 1).fill(null));

  for (let i = 0; i <= str1.length; i++) matrix[0][i] = i;
  for (let j = 0; j <= str2.length; j++) matrix[j][0] = j;

  for (let j = 1; j <= str2.length; j++) {
    for (let i = 1; i <= str1.length; i++) {
      const indicator = str1[i - 1] === str2[j - 1] ? 0 : 1;
      matrix[j][i] = Math.min(
        matrix[j][i - 1] + 1, // deletion
        matrix[j - 1][i] + 1, // insertion
        matrix[j - 1][i - 1] + indicator, // substitution
      );
    }
  }

  return matrix[str2.length][str1.length];
}

export function generateRandomPassword(length = 8) {
  const chars = 'ABCDEFGHJKMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789';
  let password = '';
  for (let i = 0; i < length; i++) {
    password += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return password;
}

export function truncateText(value: string, len: number = 24) {
  return value?.length > len ? value.slice(0, len) + '…' : value;
}

/**
 * Trim + lowercase a single string. Returns '' for whitespace-only input.
 * Optional `maxLength` truncates *after* normalizing.
 */
export function normalizeToLowercase(value: string, maxLength?: number): string;
/**
 * Trim + lowercase an array of strings, dropping empties and deduplicating.
 */
export function normalizeToLowercase(values: string[]): string[];
export function normalizeToLowercase(
  input: string | string[],
  maxLength?: number,
): string | string[] {
  if (typeof input === 'string') {
    const v = input.trim().toLowerCase();
    return maxLength != null ? v.slice(0, maxLength) : v;
  }
  const seen = new Set<string>();
  const out: string[] = [];
  for (const raw of input) {
    const v = raw.trim().toLowerCase();
    if (!v || seen.has(v)) continue;
    seen.add(v);
    out.push(v);
  }
  return out;
}

export function toTitleCase(input: string) {
  return input
    .toLowerCase()
    .split(/\s+/)
    .map((word) => {
      if (word.length === 0) return word;
      return word[0].toUpperCase() + word.slice(1);
    })
    .join(' ');
}
